import { useGT } from 'gt-react';
import { z } from 'zod';
import { Card } from '@/components/ui/card';

// Mirrors the backend render_bar_chart inputSchema. Local (not shared via
// @erp/rpc) because it's tiny and this is the only consumer.
const barChartArgsSchema = z.object({
  title: z.string().optional(),
  format: z.enum(['number', 'currency', 'percent']).optional(),
  data: z
    .array(z.object({ label: z.string(), value: z.number().finite() }))
    .min(1),
});

function formatValue(
  value: number,
  format?: 'number' | 'currency' | 'percent',
): string {
  if (format === 'currency')
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  if (format === 'percent')
    return new Intl.NumberFormat(undefined, {
      style: 'percent',
      maximumFractionDigits: 1,
    }).format(value);
  return new Intl.NumberFormat().format(value);
}

interface BarChartToolResultProps {
  /** The `render_bar_chart` tool-call args: `{ title?, format?, data }`. */
  args: Record<string, unknown>;
}

/**
 * Inline generative-UI card: renders the AI's `render_bar_chart` spec as a
 * dependency-free horizontal bar chart (Tailwind + shadcn Card only).
 */
export function BarChartToolResult({ args }: BarChartToolResultProps) {
  const t = useGT();
  const parsed = barChartArgsSchema.safeParse(args);

  const data = parsed.success ? parsed.data.data : [];
  // Scale bars to the largest magnitude (works for fractional/percent data).
  // Seed with 0 so an empty/all-zero set yields max === 0 → fallback below,
  // which also keeps the width divisor non-zero for real charts.
  const max = Math.max(0, ...data.map((d) => Math.abs(d.value)));

  if (!parsed.success || max === 0) {
    return (
      <Card>
        <p className="text-muted-foreground text-sm">
          {t('Unable to render chart.', {
            context:
              'shown when an AI-generated bar chart spec is invalid or has no data',
          })}
        </p>
      </Card>
    );
  }

  const { title, format } = parsed.data;

  return (
    <Card>
      {title && (
        <div className="text-card-foreground text-sm font-medium">{title}</div>
      )}
      <ul className="flex flex-col gap-2">
        {data.map((d, i) => (
          <li
            key={`${d.label}-${i}`}
            className="grid grid-cols-[minmax(4rem,8rem)_1fr_auto] items-center gap-2"
          >
            <span
              className="text-muted-foreground truncate text-xs"
              title={d.label}
            >
              {d.label}
            </span>
            <span className="bg-muted h-2 rounded" aria-hidden="true">
              <span
                className="bg-primary block h-full rounded"
                style={{ width: `${(Math.abs(d.value) / max) * 100}%` }}
              />
            </span>
            <span className="text-card-foreground text-xs font-medium tabular-nums">
              {formatValue(d.value, format)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
